/**
 * BulkDiscountPopover — the always-visible bulk-discount toolbar action,
 * ported from the retired Wyceny tab's `PricingTabToolbar` into the merged
 * table's toolbar (`offer-unified-table` capability, CU-86cbc3r8b). Two
 * branches survive unchanged: with rows selected it applies to that
 * selection; with nothing selected it applies to every unit in the offer
 * (the caller resolves the whole-offer set and the confirmation dialog).
 */
import * as React from 'react';
import { Percent } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import type { TFunction } from 'i18next';

export interface BulkDiscountPopoverProps {
  canEdit: boolean;
  selectedCount: number;
  /** Of `selectedCount`, how many are not in the current results (a filter hides them). */
  hiddenCount?: number;
  /**
   * True while a hidden unit's fields (needed to submit) are still being
   * resolved from the whole-offer fetch — submitting now would silently skip
   * them. Only relevant when `selectedCount > 0`.
   */
  applySelectedDisabled?: boolean;
  /** Called with the discount percent when rows are selected. */
  onApplySelected: (discountPercent: number) => void;
  /** Called with the discount percent when nothing is selected (whole-offer path). */
  onApplyAll: (discountPercent: number) => void;
  t: TFunction<'offers'>;
}

export function BulkDiscountPopover({
  canEdit,
  selectedCount,
  hiddenCount = 0,
  applySelectedDisabled = false,
  onApplySelected,
  onApplyAll,
  t,
}: BulkDiscountPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [discountValue, setDiscountValue] = React.useState('');

  if (!canEdit) return null;

  function handleSubmit() {
    const pct = parseFloat(discountValue);
    if (isNaN(pct) || pct < 0 || pct > 100) return;
    if (selectedCount > 0 && applySelectedDisabled) return;

    if (selectedCount > 0) {
      onApplySelected(pct);
    } else {
      onApplyAll(pct);
    }
    setOpen(false);
    setDiscountValue('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleSubmit();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid="bulk-discount-button"
          aria-label={t('workspace.pricingTab.toolbar.applyDiscountAriaLabel')}
        >
          <Percent className="h-4 w-4" aria-hidden="true" />
          {t('workspace.pricingTab.toolbar.applyDiscount')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" data-testid="bulk-discount-popover">
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            {selectedCount > 0
              ? t('workspace.pricingTab.toolbar.discountPopoverSelected', { count: selectedCount })
              : t('workspace.pricingTab.toolbar.discountPopoverAll')}
          </p>
          {selectedCount > 0 && hiddenCount > 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="bulk-discount-hidden-count">
              {t('workspace.pricingTab.toolbar.discountPopoverHiddenCount', {
                count: hiddenCount,
              })}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="0"
              data-testid="bulk-discount-input"
              aria-label={t('workspace.pricingTab.toolbar.discountInputAriaLabel')}
              className="h-8 text-right font-mono text-xs"
            />
            <span className="text-xs text-muted-foreground shrink-0">%</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                setOpen(false);
                setDiscountValue('');
              }}
            >
              {t('workspace.pricingTab.toolbar.discountCancel')}
            </Button>
            <Button
              size="xs"
              data-testid="bulk-discount-confirm"
              onClick={handleSubmit}
              disabled={selectedCount > 0 && applySelectedDisabled}
              aria-label={t('workspace.pricingTab.toolbar.discountSubmitAriaLabel')}
            >
              {t('workspace.pricingTab.toolbar.discountSubmit')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
